import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Archive,
  ArrowUpRight,
  CheckCircle2,
  Compass,
  FileText,
  Files,
  Loader2,
  Play,
  Trash2,
  XCircle,
} from 'lucide-react';
import {
  deleteGenerateSession,
  listGenerateSessions,
} from './generateApi';

function TypeCard({ type, onStart }) {
  const disabled = !type.available;
  const isBundle = !!type.bundle;
  const Icon = isBundle ? Files : FileText;
  const [hover, setHover] = useState(false);

  const meta = [];
  if (type.questionCount) {
    // Screening flows prune branches as you answer, so the static count is a
    // ceiling, not a promise.
    meta.push(
      type.screening
        ? `Up to ${type.questionCount} questions`
        : `${type.questionCount} question${type.questionCount === 1 ? '' : 's'}`
    );
  }
  if (isBundle && type.documentCount) {
    meta.push(`Generates ${type.documentCount} documents`);
  } else if (isBundle && type.screening) {
    // Screening flows produce a dynamic doc set (or no docs at all if the
    // outcome is a triage). Avoid promising a specific count.
    meta.push('Generates the right package');
  }
  // Every intake supports pasting notes for AI prefill now.
  meta.push('AI prefill from notes');

  return (
    <div
      className="card"
      style={{
        padding: 18,
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'default' : 'pointer',
        transition: 'transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease',
        transform: hover && !disabled ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hover && !disabled
          ? '0 6px 18px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)'
          : undefined,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
      }}
      onClick={() => !disabled && onStart(type.key)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* Top row: icon + title + affordance */}
      <div className="row" style={{ gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
        <div
          style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'var(--surface-purple)', color: 'var(--text-purple)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              {type.displayName}
            </div>
            {isBundle && (
              <span className="chip purple sq">
                Bundle
              </span>
            )}
            {disabled && (
              <span className="chip gray sq">
                Coming soon
              </span>
            )}
          </div>
        </div>
        {!disabled && (
          <div
            style={{
              color: 'var(--icon-tertiary)',
              transition: 'transform 150ms ease, color 150ms ease',
              transform: hover ? 'translate(2px, -2px)' : 'none',
              flexShrink: 0,
              marginTop: 2,
            }}
          >
            <ArrowUpRight className="w-4 h-4" />
          </div>
        )}
      </div>

      {/* Description */}
      <div
        className="muted"
        style={{ fontSize: 12.5, marginBottom: 14, lineHeight: 1.5 }}
      >
        {type.description}
      </div>

      {/* Bundle contents — small rows with doc icons */}
      {isBundle && type.documents && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {type.documents.map((d) => (
            <div
              key={d}
              className="row"
              style={{
                gap: 8,
                alignItems: 'center',
                padding: '6px 8px',
                borderRadius: 6,
                background: 'var(--surface-alpha-primary)',
                fontSize: 12,
                color: 'var(--text-secondary)',
              }}
            >
              <FileText className="w-3.5 h-3.5" style={{ color: 'var(--icon-tertiary)', flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Bottom group — footer meta pinned to the bottom so it lines up across
          cards no matter how long each description or bundle list is. The full
          "what you'll need" preview now lives on the launchpad (the intermediate
          step after clicking the card). */}
      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* Footer meta */}
        {meta.length > 0 && (
          <div
            className="muted"
            style={{
              fontSize: 11,
              paddingTop: 10,
              borderTop: '0.5px solid var(--border-primary)',
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            {meta.map((m, i) => (
              <span key={i}>{m}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        color: 'var(--text-secondary)',
        marginBottom: 12,
        marginTop: 4,
      }}
    >
      {title}
    </div>
  );
}

function TypeGrid({ types, onStart }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 16 }}>
      {types.map((t) => (
        <TypeCard key={t.key} type={t} onStart={onStart} />
      ))}
    </div>
  );
}

// Status visuals shared between the landing panel and the detail page.
function sessionStatusVisuals(session) {
  const s = session.status;
  if (s === 'in_progress') return { Icon: Play,    color: 'var(--text-blue)',     label: 'In progress' };
  if (s === 'abandoned')   return { Icon: Archive, color: 'var(--text-tertiary)', label: 'Abandoned' };
  const oc = session.outcome?.outcome;
  if (oc === 'proceed')  return { Icon: CheckCircle2,  color: 'var(--text-green)',    label: 'Proceeded' };
  if (oc === 'redirect') return { Icon: Compass,       color: 'var(--text-blue)',     label: 'Redirected' };
  if (oc === 'stop')     return { Icon: XCircle,       color: 'var(--text-critical)', label: 'Hard stop' };
  if (oc === 'escalate') return { Icon: AlertTriangle, color: 'var(--text-yellow)',   label: 'Escalated' };
  return { Icon: CheckCircle2, color: 'var(--text-secondary)', label: 'Completed' };
}

function formatRelativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 14) return `${days}d ago`;
  return d.toLocaleDateString();
}

// Small helper so 'nda' renders as 'NDA' and 'grid' as 'Grid Suite'.
// Anything else gets a basic title-case fallback so unknown future
// type keys still look reasonable.
function formatTypeKey(typeKey) {
  if (typeKey === 'grid') return 'Grid Suite';
  if (typeKey === 'nda') return 'NDA';
  if (!typeKey) return 'Unknown';
  return typeKey.charAt(0).toUpperCase() + typeKey.slice(1);
}

function SessionsPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  // URL-bound inbox mode: ?inbox=escalations | (none)
  const inboxParam = searchParams.get('inbox') || '';
  const [filter, setFilter] = useState(() =>
    inboxParam === 'escalations' ? 'escalations' : 'all'
  );

  // Keep URL in sync when the user clicks filter chips.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (filter === 'escalations') next.set('inbox', filter);
    else next.delete('inbox');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    let cancelled = false;
    listGenerateSessions()
      .then((data) => {
        if (cancelled) return;
        setSessions(data?.sessions || []);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Helpers
  const isEscalation = (s) =>
    s.status === 'completed' && s.outcome && s.outcome.outcome !== 'proceed';

  // Delete a session from the list. Confirms first; if the session has
  // generated drafts, the server's delete cascades them. We just remove
  // it from local state on success rather than re-fetching the whole list.
  async function deleteSession(s) {
    const msg = `Delete the intake for ${s.prospectName || 'this prospect'}? This cannot be undone${
      s.outcome?.outcome === 'proceed' ? ', and any drafts generated from it will also be removed' : ''
    }.`;
    if (!window.confirm(msg)) return;
    setDeletingId(s.id);
    try {
      await deleteGenerateSession(s.id);
      setSessions((prev) => prev.filter((x) => x.id !== s.id));
    } catch (err) {
      window.alert(err?.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  }

  const visible = sessions.filter((s) => {
    if (filter === 'all')         return true;
    if (filter === 'in_progress') return s.status === 'in_progress';
    if (filter === 'completed')   return s.status === 'completed';
    if (filter === 'escalations') return isEscalation(s);
    return true;
  });

  if (loading) return null;
  if (sessions.length === 0) return null;

  const isInboxMode = filter === 'escalations';

  return (
    <section style={{ marginTop: 32 }}>
      <div
        className="row"
        style={{ alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            color: 'var(--text-secondary)',
          }}
        >
          {filter === 'escalations' ? 'Intakes needing action' : 'Recent intakes'}
        </div>
        <div className="row" style={{ gap: 4, marginLeft: 'auto', flexWrap: 'wrap' }}>
          {[
            { value: 'all',          label: 'All' },
            { value: 'in_progress',  label: 'In progress' },
            { value: 'completed',    label: 'Completed' },
            { value: 'escalations',  label: 'Needs action' },
          ].map((f) => (
            <button
              key={f.value}
              type="button"
              className={`btn ${filter === f.value ? 'primary' : 'ghost'} xs`}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      {visible.length === 0 ? (
        <div className="card" style={{ padding: 20, textAlign: 'center' }}>
          <div className="muted" style={{ fontSize: 12 }}>
            {isInboxMode ? 'Nothing in this inbox right now.' : 'No sessions match this filter.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {visible.map((s) => {
            const v = sessionStatusVisuals(s);
            const needsAction = isEscalation(s);
            return (
              <Link
                key={s.id}
                to={`/intake/sessions/${s.id}`}
                className="card"
                style={{
                  padding: 12,
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto auto auto',
                  gap: 12,
                  alignItems: 'center',
                  borderLeft: needsAction ? '2px solid var(--text-yellow)' : undefined,
                }}
              >
                <div
                  style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: 'var(--surface-alpha-primary)', color: v.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <v.Icon className="w-3.5 h-3.5" />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="row" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>
                      {s.prospectName || <span className="muted" style={{ fontWeight: 400 }}>Unnamed prospect</span>}
                    </span>
                    <span className="muted" style={{ fontSize: 12 }}>·</span>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {formatTypeKey(s.typeKey)}
                    </span>
                    {needsAction && (
                      <span className="chip yellow sq">Needs action</span>
                    )}
                  </div>
                  <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                    {v.label}
                    {s.outcome?.summary && ` · ${s.outcome.summary}`}
                    {!s.isOwn && ' · created by another user'}
                  </div>
                </div>
                <div className="muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                  {formatRelativeTime(s.updatedAt)}
                </div>
                {s.status === 'in_progress' && s.isOwn ? (
                  <span className="btn primary sm" style={{ pointerEvents: 'none' }}>Resume</span>
                ) : <span />}
                {s.isOwn ? (
                  <button
                    type="button"
                    className="icon-btn sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      deleteSession(s);
                    }}
                    disabled={deletingId === s.id}
                    title="Delete this intake"
                    aria-label="Delete this intake"
                    style={{ color: 'var(--icon-tertiary)' }}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                ) : <span />}
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

function Landing({ types, loading, onStart }) {
  const available = types.filter((t) => t.available);
  const comingSoon = types.filter((t) => !t.available);

  return (
    <div className="canvas-inner animate-fade-in">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Intake</div>
          <h1 className="page-title">Start a new intake</h1>
          <div className="page-subtitle">
            Answer a few questions and we'll generate a draft from Lightspark's
            standard template. The draft lands in your contracts list for legal
            to review.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ padding: '60px 20px', textAlign: 'center' }}>
          <Loader2 className="w-5 h-5 animate-spin" style={{ margin: '0 auto 10px', color: 'var(--icon-secondary)' }} />
          <div className="muted" style={{ fontSize: 13 }}>Loading document types…</div>
        </div>
      ) : types.length === 0 ? (
        <div className="card" style={{ padding: '60px 20px', textAlign: 'center' }}>
          <div className="muted" style={{ fontSize: 13 }}>No document types available yet.</div>
        </div>
      ) : (
        <>
          {available.length > 0 && (
            <section style={{ marginBottom: comingSoon.length > 0 ? 28 : 0 }}>
              <SectionHeader title="Available now" />
              <TypeGrid types={available} onStart={onStart} />
            </section>
          )}
          {comingSoon.length > 0 && (
            <section>
              <SectionHeader title="Coming soon" />
              <TypeGrid types={comingSoon} onStart={onStart} />
            </section>
          )}
        </>
      )}

      <SessionsPanel />
    </div>
  );
}

export default Landing;
